import { useEffect, useState } from "react";
import { Dialog } from "@leylion/design-system";
import { App } from "./App";
import {
  SESSION_UPDATED_EVENT,
  clearSession,
  getAccessToken,
  getStoredPermissions
} from "./api/client";
import { PasswordRecoveryExperience } from "./auth/PasswordRecoveryExperience";
import { SESSION_EXPIRED_EVENT } from "./auth/session-expiry-boundary";
import "./cashier/pos-viewport-hardening.css";
import "./cashier/cashier-mobile-catalog-ux.css";
import "./cashier/cashier-product-name-glyph-fidelity.css";
import { FirstLoginExperience } from "./onboarding/FirstLoginExperience";
import { FirstLoginManagerPinGate } from "./onboarding/FirstLoginManagerPinGate";
import { PlatformAdministratorPanel } from "./platform/PlatformAdministratorPanel";
import { PlatformConsole } from "./platform/PlatformConsole";
import { PlatformContextEntry } from "./platform/PlatformContextEntry";
import {
  clearPlatformSession,
  getPlatformAccessToken
} from "./platform/platform-client";
import "./platform/platform-console-mobile-hardening.css";
import { PublicSignupExperience } from "./signup/PublicSignupExperience";
import "./root-app.css";

type RootRoute =
  | "signup"
  | "forgot-password"
  | "reset-password"
  | "platform"
  | "tenant-platform-route"
  | "app";

const applicationMode = import.meta.env.VITE_APP_MODE === "cashier" ? "cashier" : "full";
const tenantBlockedRoutes = new Set(["production", "release"]);
const platformPermissionPrefixes = ["production.", "release."];

function clearStaleTenantPlatformSession(): void {
  const hasPlatformPermission = getStoredPermissions().some((permission) =>
    platformPermissionPrefixes.some((prefix) =>
      permission.toLowerCase().startsWith(prefix)
    )
  );

  if (hasPlatformPermission) {
    clearSession();
  }
}

clearStaleTenantPlatformSession();

function readRootRoute(): RootRoute {
  const route = window.location.hash
    .replace(/^#\/?/, "")
    .split(/[/?]/, 1)[0]
    ?.trim()
    .toLowerCase();
  if (route === "signup") return "signup";
  if (route === "forgot-password") return "forgot-password";
  if (route === "reset-password") return "reset-password";
  if (route === "platform") return "platform";
  if (route && tenantBlockedRoutes.has(route)) return "tenant-platform-route";
  return "app";
}

function replaceHash(hash: string): void {
  const url = `${window.location.pathname}${window.location.search}${hash}`;
  window.history.replaceState(null, "", url);
  window.dispatchEvent(new HashChangeEvent("hashchange"));
}

export function RootApp() {
  const [signedIn, setSignedIn] = useState(() => Boolean(getAccessToken()));
  useEffect(() => {
    const refresh = () => setSignedIn(Boolean(getAccessToken()));
    window.addEventListener(SESSION_UPDATED_EVENT, refresh);
    window.addEventListener(SESSION_EXPIRED_EVENT, refresh);
    return () => { window.removeEventListener(SESSION_UPDATED_EVENT, refresh); window.removeEventListener(SESSION_EXPIRED_EVENT, refresh); };
  }, []);
  const [route, setRoute] = useState<RootRoute>(() => readRootRoute());
  const [appRevision, setAppRevision] = useState(0);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [platformToken, setPlatformToken] = useState<string | null>(() => getPlatformAccessToken());
  const [platformLanguage, setPlatformLanguage] = useState<"ar" | "en">("ar");
  const [platformAdminOpen, setPlatformAdminOpen] = useState(false);

  useEffect(() => {
    const syncRoute = () => setRoute(readRootRoute());
    window.addEventListener("hashchange", syncRoute);
    window.addEventListener("popstate", syncRoute);
    return () => {
      window.removeEventListener("hashchange", syncRoute);
      window.removeEventListener("popstate", syncRoute);
    };
  }, []);

  useEffect(() => {
    const handleSessionExpired = () => {
      setSessionExpired(true);
      setPlatformToken(null);
      setPlatformAdminOpen(false);
      replaceHash("#/login");
      setRoute("app");
      setAppRevision((value) => value + 1);
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, []);

  useEffect(() => {
    if (!sessionExpired) return undefined;
    const timeout = window.setTimeout(() => setSessionExpired(false), 12_000);
    return () => window.clearTimeout(timeout);
  }, [sessionExpired]);

  useEffect(() => {
    if (route !== "tenant-platform-route") return;
    replaceHash("#/pos");
    setRoute("app");
  }, [route]);

  useEffect(() => {
    if (route !== "platform" || platformToken) return;
    replaceHash(getAccessToken() ? "#/pos" : "#/login");
    setRoute("app");
  }, [route, platformToken]);

  const openApplication = () => {
    setSessionExpired(false);
    replaceHash("#/pos");
    setRoute("app");
  };

  const openSignIn = () => {
    replaceHash("#/login");
    setRoute("app");
  };

  const openRecoveryRequest = () => {
    replaceHash("#/forgot-password");
    setRoute("forgot-password");
  };

  const openPlatform = (token: string) => {
    setSessionExpired(false);
    setPlatformToken(token);
    replaceHash("#/platform");
    setRoute("platform");
  };

  const exitPlatform = () => {
    clearPlatformSession();
    setPlatformToken(null);
    setPlatformAdminOpen(false);
    replaceHash("#/pos");
    setRoute("app");
    setAppRevision((value) => value + 1);
  };

  const logoutPlatform = () => {
    clearPlatformSession();
    clearSession();
    setPlatformToken(null);
    setPlatformAdminOpen(false);
    replaceHash("#/login");
    setRoute("app");
    setAppRevision((value) => value + 1);
  };

  if (route === "tenant-platform-route") {
    return null;
  }

  if (applicationMode === "full" && route === "platform" && platformToken) {
    return (
      <>
        <PlatformConsole
          token={platformToken}
          language={platformLanguage}
          onToggleLanguage={() => setPlatformLanguage((value) => value === "ar" ? "en" : "ar")}
          onOpenAdministration={() => setPlatformAdminOpen(true)}
          onExit={exitPlatform}
          onLogout={logoutPlatform}
        />
        <Dialog
          open={platformAdminOpen}
          onClose={() => setPlatformAdminOpen(false)}
          title={platformLanguage === "ar" ? "إدارة أمان المنصة" : "Platform security administration"}
          description={platformLanguage === "ar"
            ? "PlatformOwner · Control Plane فقط · لا صلاحيات مؤسسة أو فرع"
            : "PlatformOwner · control plane only · no tenant or branch authority"}
          closeLabel={platformLanguage === "ar" ? "إغلاق إدارة أمان المنصة" : "Close platform security administration"}
          className="root-platform-admin-dialog"
        >
          <div
            data-testid="platform-admin-security"
            dir={platformLanguage === "ar" ? "rtl" : "ltr"}
          >
            <PlatformAdministratorPanel token={platformToken} language={platformLanguage} />
          </div>
        </Dialog>
      </>
    );
  }

  if (!getAccessToken()) {
    if (applicationMode === "full" && route === "signup") {
      return (
        <PublicSignupExperience
          onSignIn={openSignIn}
          onWorkspaceReady={openApplication}
        />
      );
    }

    if (route === "forgot-password" || route === "reset-password") {
      return (
        <PasswordRecoveryExperience
          mode={route === "reset-password" ? "reset" : "request"}
          onSignIn={openSignIn}
          onRequestNew={openRecoveryRequest}
        />
      );
    }
  }

  const showRecoveryShortcut = !signedIn;

  return (
    <>
      <App key={appRevision} />
      {applicationMode === "full" && route === "app" ? (
        <PlatformContextEntry language={platformLanguage} onSelected={openPlatform} />
      ) : null}
      {sessionExpired && !getAccessToken() ? (
        <div className="root-session-expired-notice" role="alert" data-testid="session-expired-notice">
          <strong>انتهت الجلسة أو تم إلغاؤها.</strong>
          <span>سجّل الدخول مرة أخرى للمتابعة. · Your session expired or was revoked. Sign in again to continue.</span>
        </div>
      ) : null}
      {showRecoveryShortcut ? (
        <button
          className="root-password-recovery-shortcut"
          type="button"
          onClick={openRecoveryRequest}
        >
          نسيت كلمة المرور؟ · Forgot password?
        </button>
      ) : null}
      {applicationMode === "full" && route === "app" ? (
        <>
          <FirstLoginExperience
            onCurrencyConfirmed={() => setAppRevision((value) => value + 1)}
          />
          <FirstLoginManagerPinGate />
        </>
      ) : null}
    </>
  );
}
